/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { MdSearch, MdArrowBack } from "react-icons/md";
import HomeService from "../../../services/home.service";
import { Constants } from "../../../utils";

const ViewFinish = () => {
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [loadingInitial, setLoadingInitial] = useState(true);

  const timerRef = useRef(null);

  const load = useCallback(
    async (reload = false, currentSearch = search) => {
      const currentPage = reload ? 1 : page;
      setLoading(true);
      if (reload) setPage(1);

      try {
        const res = await HomeService.admin.get({
          page: currentPage,
          withoutLoading: true,
          search: currentSearch,
          status: Constants.PACKAGE_STATUS.FINISH,
        });

        setData((prev) =>
          reload ? res.packages.rows : [...prev, ...res.packages.rows],
        );
        setLastPage(res.packages.count);
      } catch (error) {
        console.error(error);
        toast.error("Error al cargar los contenedores finalizados");
      } finally {
        setLoading(false);
        setLoadingInitial(false);
      }
    },
    [page, search],
  );

  useEffect(() => {
    load(true);
  }, []);

  useEffect(() => {
    if (page > 1) load();
  }, [page]);

  const handleSearch = (e) => {
    const val = e.target.value;
    setSearch(val);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => load(true, val), 1000);
  };

  const handleScroll = (e) => {
    const { scrollTop, clientHeight, scrollHeight } = e.target;
    if (
      scrollHeight - scrollTop <= clientHeight * 1.2 &&
      page < lastPage &&
      !loading
    ) {
      setPage((prev) => prev + 1);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const viewContent = (item) => {
    navigate("/admin/packages/view-content", { state: { item } });
  };

  const viewActivity = (item) => {
    navigate("/admin/packages/view-activity", { state: { item } });
  };

  return (
    <div
      className="flex flex-col h-[calc(100vh-5rem)] overflow-y-auto px-4 py-6 md:px-8"
      onScroll={handleScroll}
    >
      {/* HEADER & BUSCADOR */}
      <div className="max-w-5xl mx-auto w-full mb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-sm font-bold text-[#1D3B8B] hover:underline mb-4"
        >
          <MdArrowBack size={18} />
          <span>Volver</span>
        </button>

        <div className="relative w-full mb-6">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MdSearch size={20} className="text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Buscar por BL o Código Interno"
            className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl bg-white shadow-sm focus:ring-2 focus:ring-[#1D3B8B] focus:border-transparent outline-none transition-all text-sm"
            value={search}
            onChange={handleSearch}
          />
        </div>

        <h1 className="text-xl font-bold text-gray-800 uppercase tracking-wide">
          Contenedor Finalizados
        </h1>
      </div>

      {/* LISTADO DE ITEMS */}
      <div className="max-w-5xl mx-auto w-full space-y-4">
        {loadingInitial && data.length === 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center text-[#1D3B8B] font-medium">
            Cargando...
          </div>
        )}

        {!loadingInitial && data.length === 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center text-gray-400">
            No se han encontrado registros
          </div>
        )}

        {data.map((item, index) => (
          <div
            key={item.id || index}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 transition-shadow hover:shadow-md"
          >
            <div className="flex justify-between items-start mb-3 border-b border-gray-50 pb-3">
              <div>
                {item.internal_code && (
                  <span className="text-xs text-gray-500 font-medium block">
                    {item.internal_code}
                  </span>
                )}
                <span className="text-base font-bold text-gray-900 uppercase block">
                  {item.number}
                </span>
                <span className="text-xs font-bold text-purple-600 uppercase block mt-0.5">
                  {item.code}
                </span>
              </div>

              <div className="text-right text-xs text-gray-500">
                {(item.last_activity_date || item.created_at) && (
                  <span>
                    <strong className="text-gray-700">Date:</strong>{" "}
                    {formatDate(item.last_activity_date || item.created_at)}
                  </span>
                )}
              </div>
            </div>

            <div className="space-y-1 text-sm text-gray-600 mb-4">
              <p>
                <strong className="text-gray-800">Último Movimiento:</strong>{" "}
                {item.last_activity || "-"}
              </p>
              <p>
                <strong className="text-gray-800">Ubicación:</strong>{" "}
                {item.location || "-"}
              </p>
            </div>

            <div className="flex justify-between items-center border-t border-gray-50 pt-3 text-sm">
              <button
                type="button"
                onClick={() => viewActivity(item)}
                className="font-bold text-green-600 hover:text-green-700 transition-colors"
              >
                Ver actividad
              </button>
              <button
                type="button"
                onClick={() => viewContent(item)}
                className="font-bold text-[#1D3B8B] hover:text-[#152a66] transition-colors"
              >
                Ver contenido
              </button>
            </div>
          </div>
        ))}

        {loading && lastPage > 1 && (
          <div className="py-4 text-center text-[#1D3B8B] font-medium">
            Cargando más registros...
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewFinish;
